import React from 'react'
import clsx from 'clsx'
import { Box, Typography } from '@mui/material'
import { getRoleTitle } from 'src/domain/role'
import { getTenantStatusTitle } from 'src/domain/tenant'
import {
  ActivityLog,
  RoleEnum,
  TenantStatusEnum,
} from 'src/slices/services/api'
import { formatDateWithTimeAndWeekday } from 'src/utils/date'
import { stringifyValue } from 'src/utils/string'
import styles from './styles.module.scss'

export type ActivityLogItemProps = {
  orgCode: string
  log: ActivityLog
  eventTypeLabel?: string
  showDiff?: boolean
} & React.StyledProps

const renderValueChange = (before?: string | null, after?: string | null) => {
  const safeAfter = after ?? ''
  if (before) {
    return (
      <>
        <span className={styles.before}>{before}</span>→
        <span className={styles.after}>{safeAfter}</span>
      </>
    )
  }
  return <span className={styles.safeAfter}>{safeAfter}</span>
}

type DiffRows = {
  key: string
  before: string | null
  after: string
}[]

type FieldConfig = {
  key: string
  afterStateKey?: string // camelCase変換用（省略時はkeyと同じ）
  transform?: (value: string | null) => string | null
}

const toActiveTitle = (value: string | null): string | null => {
  if (value === null) return null
  const map: Record<string, string> = { true: '利用中', false: '利用停止' }
  return map[value] ?? value
}

const createToRoleTitle =
  (orgCode: string) =>
  (value: string | null): string | null => {
    if (!value) return null
    return getRoleTitle(orgCode, value as RoleEnum) || value
  }

const toAllStoresPermissionTitle = (value: string | null): string | null => {
  if (value === null) return null
  const map: Record<string, string> = { true: '全店舗', false: '指定店舗のみ' }
  return map[value] ?? value
}

const toStoreCount = (value: string | null): string | null => {
  if (!value) return null
  try {
    const arr = JSON.parse(value) as unknown[]
    if (arr.length === 0) return null
    return `${arr.length}店舗`
  } catch {
    return value
  }
}

const toStatusTitle = (value: string | null): string | null => {
  if (!value) return null
  const statuses = Object.values(TenantStatusEnum) as string[]
  return statuses.includes(value)
    ? getTenantStatusTitle(value as TenantStatusEnum)
    : value
}

const getMemberFields = (orgCode: string): FieldConfig[] => [
  { key: 'active', transform: toActiveTitle },
  { key: 'role', transform: createToRoleTitle(orgCode) },
  { key: 'name' },
  { key: 'email' },
  {
    key: 'has_all_stores_permission',
    afterStateKey: 'hasAllStoresPermission',
    transform: toAllStoresPermissionTitle,
  },
  {
    key: 'assigned_store_ids',
    afterStateKey: 'assignedStoreIds',
    transform: toStoreCount,
  },
]

const TENANT_FIELDS: FieldConfig[] = [
  { key: 'status', transform: toStatusTitle },
  { key: 'code' },
  { key: 'name' },
]

const getFieldConfigs = (
  orgCode: string
): Record<string, FieldConfig[] | undefined> => ({
  member: getMemberFields(orgCode),
  tenant: TENANT_FIELDS,
})

type AfterStateValue = string | boolean | string[]
type AfterState = Record<string, AfterStateValue | undefined>

const getAfterStateValue = (
  afterState: AfterState,
  key: string
): AfterStateValue | undefined => afterState[key]

const buildItems = (log: ActivityLog, fieldConfigs: FieldConfig[]) => {
  return log.items
    .map((item) => {
      const rows: DiffRows = fieldConfigs
        .map((config) => {
          const { key, afterStateKey = key, transform } = config
          const diff = item.diff.find((d) => d.field === key)
          const rawBefore = stringifyValue(diff?.before) ?? null
          const rawAfter =
            stringifyValue(diff?.after) ??
            stringifyValue(
              getAfterStateValue(
                (item.afterState ?? {}) as AfterState,
                afterStateKey
              )
            )

          const before = transform ? transform(rawBefore) : rawBefore
          const after = transform ? transform(rawAfter) : rawAfter

          if (!after) return null
          return { key, before, after }
        })
        .filter(Boolean) as DiffRows

      return { entityId: item.entityId, rows }
    })
    .filter((item) => item.rows.length > 0)
}

const ActivityLogItem: React.StyledFC<ActivityLogItemProps> = ({
  className,
  orgCode,
  log,
  eventTypeLabel,
  showDiff = true,
}: ActivityLogItemProps) => {
  const formattedDate = formatDateWithTimeAndWeekday(log.createdAt)
  const fieldConfigs = getFieldConfigs(orgCode)[log.entityType]
  const items = fieldConfigs ? buildItems(log, fieldConfigs) : []

  return (
    <Box className={clsx(styles.container, className)}>
      <Box className={styles.header}>
        <Typography className={styles.date}>{formattedDate}</Typography>
      </Box>
      <Box className={styles.emailRow}>
        <Typography className={styles.email}>{log.changedBy.email}</Typography>
        {eventTypeLabel && (
          <Typography component="span" className={styles.eventLabel}>
            {eventTypeLabel}
          </Typography>
        )}
      </Box>
      {showDiff && items.length > 0 && (
        <>
          {items.map((item) => (
            <Box
              key={item.entityId ?? log.operationId}
              className={styles.diffList}
            >
              {item.rows.map((row) => (
                <Typography
                  key={`${row.key}-${row.after}`}
                  className={styles.diffItem}
                >
                  ・{renderValueChange(row.before, row.after)}
                </Typography>
              ))}
            </Box>
          ))}
        </>
      )}
      {showDiff &&
        !fieldConfigs &&
        log.items.map((item) => (
          <Box
            key={item.entityId ?? log.operationId}
            className={styles.diffList}
          >
            {item.diff.map((diff) => (
              <Typography
                key={`${diff.field}-${stringifyValue(diff.after)}`}
                className={styles.diffItem}
              >
                ・
                {renderValueChange(
                  stringifyValue(diff.before),
                  stringifyValue(diff.after)
                )}
              </Typography>
            ))}
          </Box>
        ))}
    </Box>
  )
}

export default ActivityLogItem
